import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Users } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { SocialAuthIntent, SocialSignupRole } from '@/types/auth';

interface SocialAccountTypeDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onConfirm: (_intent: Required<Pick<SocialAuthIntent, 'desiredRole'>> & SocialAuthIntent) => Promise<void> | void;
  initialRole?: SocialSignupRole | null;
  initialTeamName?: string;
  initialTeamSeats?: number | null;
  initialTeamDescription?: string;
  defaultTeamSeats?: number;
  isSubmitting?: boolean;
}

type RoleValue = SocialSignupRole | '';

const TEAM_SEAT_MIN = 1;
const TEAM_SEAT_MAX = 500;

export function SocialAccountTypeDialog({
  open,
  onOpenChange,
  onConfirm,
  initialRole = null,
  initialTeamName = '',
  initialTeamSeats = null,
  initialTeamDescription = '',
  defaultTeamSeats = 10,
  isSubmitting = false,
}: SocialAccountTypeDialogProps) {
  const { t } = useTranslation();
  const [role, setRole] = useState<RoleValue>(initialRole ?? '');
  const [teamName, setTeamName] = useState<string>(initialTeamName ?? '');
  const [teamSeats, setTeamSeats] = useState<string>(
    initialTeamSeats ? String(initialTeamSeats) : String(defaultTeamSeats)
  );
  const [teamDescription, setTeamDescription] = useState<string>(initialTeamDescription ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRole(initialRole ?? '');
      setTeamName(initialTeamName ?? '');
      setTeamSeats(initialTeamSeats ? String(initialTeamSeats) : String(defaultTeamSeats));
      setTeamDescription(initialTeamDescription ?? '');
      setError(null);
    }
  }, [open, initialRole, initialTeamName, initialTeamSeats, initialTeamDescription, defaultTeamSeats]);

  useEffect(() => {
    setError(null);
  }, [role, teamName, teamSeats, teamDescription]);

  const parsedTeamSeats = useMemo(() => {
    const value = parseInt(teamSeats, 10);
    if (Number.isNaN(value)) {
      return undefined;
    }
    return Math.min(Math.max(value, TEAM_SEAT_MIN), TEAM_SEAT_MAX);
  }, [teamSeats]);

  const handleConfirm = async () => {
    if (!role) {
      setError(t('auth.socialRoleModal.validation.selectRole'));
      return;
    }

    if (role === 'team_owner' && !teamName.trim()) {
      setError(t('auth.socialRoleModal.validation.teamNameRequired'));
      return;
    }

    const intent: Required<Pick<SocialAuthIntent, 'desiredRole'>> & SocialAuthIntent = {
      desiredRole: role,
      teamName: role === 'team_owner' ? teamName.trim() : undefined,
      teamSeats: role === 'team_owner' ? parsedTeamSeats ?? defaultTeamSeats : undefined,
      teamDescription:
        role === 'team_owner' && teamDescription.trim().length > 0 ? teamDescription.trim() : undefined,
    };

    await onConfirm(intent);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="space-y-1">
          <DialogTitle>{t('auth.socialRoleModal.title')}</DialogTitle>
          <DialogDescription>{t('auth.socialRoleModal.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              {t('auth.socialRoleModal.lead')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('auth.socialRoleModal.helper')}
            </p>
          </div>

          <RadioGroup
            value={role}
            onValueChange={(value) => setRole(value as RoleValue)}
            className="grid gap-3 sm:grid-cols-2"
          >
            <Label
              htmlFor="modal-role-individual"
              className={cn(
                'flex cursor-pointer flex-col space-y-3 rounded-lg border p-4 transition hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                role === 'individual' && 'border-primary ring-1 ring-primary'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="rounded-full bg-primary/10 p-2 text-primary">
                    <User className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-medium">{t('auth.socialRoleModal.individual.title')}</span>
                </div>
                <RadioGroupItem id="modal-role-individual" value="individual" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('auth.socialRoleModal.individual.description')}
              </p>
            </Label>

            <Label
              htmlFor="modal-role-team-owner"
              className={cn(
                'flex cursor-pointer flex-col space-y-3 rounded-lg border p-4 transition hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                role === 'team_owner' && 'border-primary ring-1 ring-primary'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="rounded-full bg-primary/10 p-2 text-primary">
                    <Users className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-medium">{t('auth.socialRoleModal.team.title')}</span>
                </div>
                <RadioGroupItem id="modal-role-team-owner" value="team_owner" />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('auth.socialRoleModal.team.description')}
              </p>
            </Label>
          </RadioGroup>

          {role === 'team_owner' ? (
            <div className="space-y-3 rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="modal-team-name">{t('auth.socialRoleModal.team.nameLabel')}</Label>
                <Input
                  id="modal-team-name"
                  value={teamName}
                  onChange={(event) => setTeamName(event.target.value)}
                  placeholder={t('auth.socialRoleModal.team.namePlaceholder')}
                  autoComplete="organization"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-[minmax(0,_160px)_1fr]">
                <div className="space-y-1">
                  <Label htmlFor="modal-team-seats">{t('auth.socialRoleModal.team.sizeLabel')}</Label>
                  <Input
                    id="modal-team-seats"
                    type="number"
                    min={TEAM_SEAT_MIN}
                    max={TEAM_SEAT_MAX}
                    value={teamSeats}
                    onChange={(event) => setTeamSeats(event.target.value)}
                    placeholder={String(defaultTeamSeats)}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="modal-team-description">
                    {t('auth.socialRoleModal.team.descriptionLabel')}
                  </Label>
                  <Textarea
                    id="modal-team-description"
                    value={teamDescription}
                    onChange={(event) => setTeamDescription(event.target.value)}
                    placeholder={t('auth.socialRoleModal.team.descriptionPlaceholder')}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('auth.socialRoleModal.team.descriptionHelper')}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter className="flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0">
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? t('auth.socialRoleModal.ctaLoading') : t('auth.socialRoleModal.cta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type { SocialAccountTypeDialogProps };
